import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, CircleCheck, CircleX, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | timeout | error

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("paid");
          return;
        }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries >= 8) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen grid place-items-center px-4" data-testid="payment-success">
      <div className="bg-card border-2 border-[hsl(var(--stadium-yellow))] p-10 max-w-md text-center">
        {state === "checking" && (
          <>
            <Loader2 size={34} className="mx-auto text-primary animate-spin mb-4" />
            <h1 className="font-display text-3xl uppercase">Confirming payment…</h1>
            <p className="text-sm text-muted-foreground mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <Crown size={38} className="mx-auto text-[hsl(var(--stadium-yellow))] mb-3" />
            <CircleCheck size={22} className="mx-auto text-[hsl(var(--pitch-green))] mb-3" />
            <h1 className="font-display text-3xl uppercase">You're Premium!</h1>
            <p className="text-sm text-muted-foreground mt-2">Stats, squads, win probabilities and unlimited mini-leagues are unlocked.</p>
            <Button asChild className="mt-6 rounded-none uppercase text-xs font-bold" data-testid="success-continue">
              <Link to="/app">Back to the desk</Link>
            </Button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <CircleX size={34} className="mx-auto text-muted-foreground mb-3" />
            <h1 className="font-display text-3xl uppercase">Still processing</h1>
            <p className="text-sm text-muted-foreground mt-2">
              If you completed payment, your Premium will activate shortly. Refresh the app in a minute.
            </p>
            <Button asChild className="mt-6 rounded-none uppercase text-xs font-bold">
              <Link to="/app">Back to the desk</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen grid place-items-center px-4" data-testid="payment-cancel">
      <div className="bg-card border-2 border-border p-10 max-w-md text-center">
        <CircleX size={34} className="mx-auto text-muted-foreground mb-3" />
        <h1 className="font-display text-3xl uppercase">Checkout cancelled</h1>
        <p className="text-sm text-muted-foreground mt-2">No worries — you can upgrade anytime from the app.</p>
        <Button asChild className="mt-6 rounded-none uppercase text-xs font-bold">
          <Link to="/app">Back to the desk</Link>
        </Button>
      </div>
    </div>
  );
}
